import React, {
  Suspense,
  lazy,
  useLayoutEffect,
  useSyncExternalStore,
} from 'react';
import { createPortal } from 'react-dom';
import Popups from '@components/popups/Popups';
import Toasts from '@components/Toasts/Toasts';
import { PopupName } from '@hooks/popups/usePopups';
import { retryImport } from '@utils/retryImport';
import IslandErrorBoundary from './IslandErrorBoundary';
import IslandProviders from './IslandProviders';
import { IslandHost, getHosts, subscribeHosts } from './hosts';

const ISLAND_POPUPS: PopupName[] = [
  'SubscriptionPopup',
  'TrialMonthlyRebillPopup',
  'RescuePopup',
  'AnnualPlanPopup',
  'WordsPricesPopup',
  'PayAsYouGoPopup',
];

// Shown only after a crash, and its `Button` pulls in the whole icon set — kept
// out of island.js as its own chunk.
const AppError = lazy(() =>
  retryImport(
    () =>
      import(
        /* webpackChunkName: "app-error" */ '@components/common/AppError/AppError'
      )
  )
);

type IslandPortalProps = {
  host: IslandHost;
};

type PlaceholderRemoverProps = {
  placeholderNodes: ChildNode[];
};

// A portal appends to the host instead of clearing it the way `createRoot`
// does, so the placeholder is removed by hand — before paint, no flash. It sits
// next to the island *inside* its `Suspense` boundary: islands are `React.lazy`
// chunks (see `registry.ts`), and a suspended boundary commits nothing, so the
// server-rendered skeleton stays up until the island's code has arrived and it
// renders in the same commit.
const PlaceholderRemover: React.FC<PlaceholderRemoverProps> = ({
  placeholderNodes,
}) => {
  useLayoutEffect(() => {
    placeholderNodes.forEach((node) => node.remove());
  }, [placeholderNodes]);

  return null;
};

const IslandPortal: React.FC<IslandPortalProps> = ({ host }) => {
  const { el, Component, props, placeholderNodes } = host;

  // Inside the providers (unlike the root boundary in `mount.ts`), so the
  // fallback can use intl. An island that crashes on its first render never
  // removed the placeholder — the fallback does, so it does not sit under the
  // skeleton. The fallback has a boundary of its own: a failed `AppError` chunk
  // must not reach the root boundary and take every island down.
  return createPortal(
    <IslandErrorBoundary
      fallback={
        <IslandErrorBoundary>
          <Suspense fallback={null}>
            <AppError />
          </Suspense>
          <PlaceholderRemover placeholderNodes={placeholderNodes} />
        </IslandErrorBoundary>
      }>
      <Suspense fallback={null}>
        <Component {...props} />
        <PlaceholderRemover placeholderNodes={placeholderNodes} />
      </Suspense>
    </IslandErrorBoundary>,
    el
  );
};

/**
 * The single React root of the island bundle. Every island is a portal into its
 * own host element, so they all share one provider stack (contexts, router,
 * query cache) — exactly like routes under the SPA's `ProvidersWrapper`, which
 * is also why `Popups` / `Toasts` are rendered here, once per page.
 */
const IslandRoot: React.FC = () => {
  const hosts = useSyncExternalStore(subscribeHosts, getHosts);

  return (
    <IslandProviders>
      {hosts.map((host) => (
        <IslandPortal
          key={host.id}
          host={host}
        />
      ))}
      <Popups names={ISLAND_POPUPS} />
      <Toasts />
    </IslandProviders>
  );
};

export default IslandRoot;
